import { memo } from "react"
import type { AIProvider } from "@acme/util"

/**
 * Provider branding for the model picker and chat header.
 *
 * `resolveProvider` accepts either a provider key ('openai') or a raw model ID
 * ('gpt-5.6-sol') so callers can pass whichever they have on hand.
 */
export const resolveProvider = (value: string): AIProvider => {
    const v = (value || '').toLowerCase()

    if (['openai', 'anthropic', 'google', 'xai', 'deepseek', 'glm', 'kimi', 'qwen'].includes(v)) {
        return v as AIProvider
    }
    if (v.startsWith('claude')) return 'anthropic'
    if (v.startsWith('gemini')) return 'google'
    if (v.startsWith('grok')) return 'xai'
    if (v.startsWith('deepseek')) return 'deepseek'
    if (v.startsWith('glm')) return 'glm'
    if (v.startsWith('kimi') || v.startsWith('moonshot')) return 'kimi'
    if (v.startsWith('qwen')) return 'qwen'
    return 'openai'
}

export const PROVIDER_LABELS: Record<AIProvider, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google Gemini',
    xai: 'xAI Grok',
    deepseek: 'DeepSeek',
    glm: 'Z.AI · GLM',
    kimi: 'Moonshot · Kimi',
    qwen: 'Alibaba · Qwen',
}

/**
 * Accent colour per provider. Each pair is legible on both light and dark
 * surfaces — note xAI previously used bare `text-white`, which disappeared
 * against the light-mode panel.
 */
export const PROVIDER_ACCENT: Record<AIProvider, string> = {
    openai: 'text-emerald-600 dark:text-emerald-400',
    anthropic: 'text-orange-600 dark:text-orange-400',
    google: 'text-blue-600 dark:text-blue-400',
    xai: 'text-neutral-800 dark:text-neutral-100',
    deepseek: 'text-cyan-600 dark:text-cyan-400',
    glm: 'text-indigo-600 dark:text-indigo-400',
    kimi: 'text-fuchsia-600 dark:text-fuchsia-400',
    qwen: 'text-rose-600 dark:text-rose-400',
}

const OpenAIIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <path
            d="M12.784 1.442a.8.8 0 0 0-1.569 0l-.191.953a.8.8 0 0 1-.628.628l-.953.19a.8.8 0 0 0 0 1.57l.953.19a.8.8 0 0 1 .628.629l.19.953a.8.8 0 0 0 1.57 0l.19-.953a.8.8 0 0 1 .629-.628l.953-.19a.8.8 0 0 0 0-1.57l-.953-.19a.8.8 0 0 1-.628-.629l-.19-.953h-.002ZM5.559 4.546a.8.8 0 0 0-1.519 0l-.546 1.64a.8.8 0 0 1-.507.507l-1.64.546a.8.8 0 0 0 0 1.519l1.64.547a.8.8 0 0 1 .507.505l.546 1.641a.8.8 0 0 0 1.519 0l.546-1.64a.8.8 0 0 1 .506-.507l1.641-.546a.8.8 0 0 0 0-1.519l-1.64-.546a.8.8 0 0 1-.507-.506L5.56 4.546Zm5.6 6.4a.8.8 0 0 0-1.519 0l-.147.44a.8.8 0 0 1-.505.507l-.441.146a.8.8 0 0 0 0 1.519l.44.146a.8.8 0 0 1 .507.506l.146.441a.8.8 0 0 0 1.519 0l.147-.44a.8.8 0 0 1 .506-.507l.44-.146a.8.8 0 0 0 0-1.519l-.44-.147a.8.8 0 0 1-.507-.505l-.146-.441Z"
            fill="currentColor"
        />
    </svg>
)

const AnthropicIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <path d="M4.71 2.5h2.2l4.38 11h-2.3l-.9-2.37H4.02l-.9 2.37H.83L4.71 2.5Zm.9 2.62L4.7 9.24h2.9L6.7 5.12h-1.1Z" fill="currentColor" />
        <path d="M11.2 2.5h2.2l1.77 11h-2.2L11.2 2.5Z" fill="currentColor" opacity=".55" />
    </svg>
)

const GeminiIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <path d="M8 0l1.5 6.5L16 8l-6.5 1.5L8 16l-1.5-6.5L0 8l6.5-1.5L8 0Z" fill="currentColor" />
    </svg>
)

const GrokIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <path d="M2.5 2.5L8 8m0 0l5.5-5.5M8 8l-5.5 5.5M8 8l5.5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

const DeepSeekIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <path d="M1 8s2-4 4.5-4S8 12 10.5 12 15 8 15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

const GlmIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <path d="M8 1.4 14 4.6v6.8L8 14.6 2 11.4V4.6L8 1.4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M8 5.2 11 6.8v3.2L8 11.6 5 10V6.8l3-1.6Z" fill="currentColor" />
    </svg>
)

const KimiIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <path d="M13.6 10.4A5.6 5.6 0 0 1 6 2.6a6 6 0 1 0 7.6 7.8Z" fill="currentColor" />
    </svg>
)

const QwenIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <path d="M3 5.2 8 2.3l5 2.9v5.6L8 13.7l-5-2.9V5.2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M8 6v4.6M5.6 7.4v2.2M10.4 7.4v2.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
)

const ICONS: Record<AIProvider, () => JSX.Element> = {
    openai: OpenAIIcon,
    anthropic: AnthropicIcon,
    google: GeminiIcon,
    xai: GrokIcon,
    deepseek: DeepSeekIcon,
    glm: GlmIcon,
    kimi: KimiIcon,
    qwen: QwenIcon,
}

export type ProviderIconProps = {
    /** Provider key or a raw model ID. */
    model: string
    className?: string
}

export const ProviderIcon = memo(({ model, className = '' }: ProviderIconProps) => {
    const provider = resolveProvider(model)
    const Icon = ICONS[provider]
    return (
        <span className={`shrink-0 ${PROVIDER_ACCENT[provider]} ${className}`} title={PROVIDER_LABELS[provider]}>
            <Icon />
        </span>
    )
})
ProviderIcon.displayName = 'ProviderIcon'
